import { forwardRef, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Image } from "native-base";
import { Animated, TouchableOpacity, View } from "react-native";
import { Bell } from "assets";
import styles from "./styles";

const WIGGLE_DURATION_MS = 2000;
const WIGGLE_STEP_MS = 60;
const BELL_ICON_SIZE = 24;
const BELL_TOUCH_TARGET_SIZE = 40;

function shouldRenderBellOverlay(hasNotifications) {
  return hasNotifications ? <View testID="notification-unread-dot" style={styles.bellOverlay} /> : null;
}

const NotificationBell = forwardRef(({ hasNotifications, ...touchableProps }, ref) => {
  const rotation = useRef(new Animated.Value(0)).current;
  const wiggleLoopRef = useRef(null);
  const stopTimeoutRef = useRef(null);

  const stopWiggle = useCallback(() => {
    if (wiggleLoopRef.current) {
      wiggleLoopRef.current.stop();
      wiggleLoopRef.current = null;
    }
    rotation.setValue(0);
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
  }, [rotation]);

  const startWiggle = useCallback(() => {
    stopWiggle();

    const sequence = Animated.sequence([
      Animated.timing(rotation, {
        toValue: 1,
        duration: WIGGLE_STEP_MS,
        useNativeDriver: true,
      }),
      Animated.timing(rotation, {
        toValue: -1,
        duration: WIGGLE_STEP_MS,
        useNativeDriver: true,
      }),
      Animated.timing(rotation, {
        toValue: 0,
        duration: WIGGLE_STEP_MS,
        useNativeDriver: true,
      }),
    ]);

    wiggleLoopRef.current = Animated.loop(sequence);
    wiggleLoopRef.current.start();

    stopTimeoutRef.current = setTimeout(() => {
      stopWiggle();
    }, WIGGLE_DURATION_MS);
  }, [rotation, stopWiggle]);

  useEffect(() => {
    if (hasNotifications) {
      startWiggle();
    } else {
      stopWiggle();
    }
    return () => stopWiggle();
  }, [hasNotifications, startWiggle, stopWiggle]);

  const rotationDeg = rotation.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: ["-12deg", "0deg", "12deg"],
  });
  return (
    <TouchableOpacity
      accessibilityLabel="Notifications"
      {...touchableProps}
      ref={ref}
      style={[
        {
          alignItems: "center",
          justifyContent: "center",
          width: BELL_TOUCH_TARGET_SIZE,
          height: BELL_TOUCH_TARGET_SIZE,
        },
        touchableProps.style,
      ]}
    >
      <Animated.View style={{ transform: [{ rotate: rotationDeg }] }}>
        <Image
          source={Bell}
          alt="notifications"
          resizeMode="contain"
          style={{ width: BELL_ICON_SIZE, height: BELL_ICON_SIZE }}
        />
        {shouldRenderBellOverlay(hasNotifications)}
      </Animated.View>
    </TouchableOpacity>
  );
});

NotificationBell.propTypes = {
  hasNotifications: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

NotificationBell.defaultProps = {
  hasNotifications: false,
  style: undefined,
};

export default NotificationBell;

NotificationBell.displayName = "NotificationBell";
